import React from "react";

import { EntityType } from "constants/index";
import { usePrompt } from "hooks";
import { useClientActions } from "store";
import { IEntityTag } from "store/actions/deal-actions/tag-entity-actions";
import { FileRepoStatus, IEntityTagData } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { ClassificationPrompt } from "../../Deal-workspace/Workspace-metadata-components/Cells/Prompts/ClassificationPrompt";
import { CurrencyPrompt } from "../../Deal-workspace/Workspace-metadata-components/Cells/Prompts/CurrencyPrompt";
import { EffectiveDatePrompt } from "../../Deal-workspace/Workspace-metadata-components/Cells/Prompts/EffectiveDatePrompt";
import { OperationsStatusPrompt } from "../../Deal-workspace/Workspace-metadata-components/Cells/Prompts/OperationslStatusPrompt";
import { useClientWorkspaceCtx } from "../context/ClientWorkspace-context";
import { ClientInvestorPrompt } from "../Workspace-metadata-components/Cells/Prompts/ClientInvestorPrompt";

export const useUpdateCellHook = (_refetchFolderMetadata: () => Promise<void>) => {
  const { updateClientWorkspaceFileAction } = useClientActions();
  const { patchDetail } = useClientWorkspaceCtx();
  const openPrompt = usePrompt();

  const handleClassificationUpdate = async (
    e: React.MouseEvent<HTMLDivElement>,
    fileId: number,
    currentClassification: string | null
  ) => {
    e.stopPropagation();
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: ClassificationPrompt,
      props: { currentClassification }
    });
    if (!result) return;
    await updateClientWorkspaceFileAction({ fileId, document_type: result.classification });
    patchDetail(fileId, EntityType.FILE, { document_type: result.classification });
  };

  const handleOperationsStatusUpdate = async (
    e: React.MouseEvent<HTMLDivElement>,
    fileId: number,
    currentOperationsStatus: string | null
  ) => {
    e.stopPropagation();
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: OperationsStatusPrompt,
      props: { currentStatus: currentOperationsStatus as FileRepoStatus | null }
    });
    if (!result) return;
    await updateClientWorkspaceFileAction({ fileId, file_status: result.status });
    patchDetail(fileId, EntityType.FILE, { file_status: result.status });
  };

  const handleBulkClassificationUpdate = async (anchorEl: HTMLElement | null, fileIds: number[]) => {
    if (!fileIds.length) return;
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: anchorEl as HTMLElement,
      Component: ClassificationPrompt,
      props: { currentClassification: null }
    });
    if (!result) return;
    const outcomes = await Promise.allSettled(
      fileIds.map((fileId) => updateClientWorkspaceFileAction({ fileId, document_type: result.classification }))
    );
    outcomes.forEach((outcome, i) => {
      if (outcome.status === "fulfilled")
        patchDetail(fileIds[i], EntityType.FILE, { document_type: result.classification });
    });
  };

  const handleBulkStatusUpdate = async (anchorEl: HTMLElement | null, fileIds: number[]) => {
    if (!fileIds.length) return;
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: anchorEl as HTMLElement,
      Component: OperationsStatusPrompt,
      props: { currentStatus: null as FileRepoStatus | null }
    });
    if (!result) return;
    const outcomes = await Promise.allSettled(
      fileIds.map((fileId) => updateClientWorkspaceFileAction({ fileId, file_status: result.status }))
    );
    outcomes.forEach((outcome, i) => {
      if (outcome.status === "fulfilled") patchDetail(fileIds[i], EntityType.FILE, { file_status: result.status });
    });
  };

  const handleInvestorUpdate = async ({
    e,
    fileId,
    currentInvestorId
  }: {
    e: React.MouseEvent<HTMLDivElement>;
    fileId: number;
    currentInvestorId: number | null;
    dealId?: number;
    allInvestors?: boolean;
    splitByCommitment?: boolean;
    investorIds?: number[];
    entireCommitment?: boolean;
  }) => {
    e.stopPropagation();
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: ClientInvestorPrompt,
      props: { currentInvestorId }
    });
    if (!result) return;
    await updateClientWorkspaceFileAction({
      fileId,
      investor_id: result.investor?.id ?? null,
      all_investors: false,
      split_by_commitment: false,
      investor_ids: [],
      group_commitment: false,
      entire_commitment: false
    });
    patchDetail(fileId, EntityType.FILE, {
      investor: result.investor,
      investor_ids: [],
      all_investors: false,
      group_commitment: false,
      entire_commitment: false
    });
  };

  const handleTagsSync = (entityId: number, entityType: string, tags: IEntityTag[]) => {
    const entity_tag_data: IEntityTagData[] = tags.map((tag) => ({ investor_id: tag.id, name: tag.name }));
    patchDetail(entityId, entityType, { entity_tag_data });
  };

  const handleEffectiveDateUpdate = async (
    e: React.MouseEvent<HTMLDivElement>,
    fileId: number,
    currentEffectiveDate: string | null
  ) => {
    e.stopPropagation();
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: EffectiveDatePrompt,
      props: { currentDate: currentEffectiveDate }
    });
    if (!result) return;
    const effective_date = result.effectiveDate ? toIsoMidday(result.effectiveDate) : null;
    await updateClientWorkspaceFileAction({ fileId, effective_date });
    patchDetail(fileId, EntityType.FILE, { effective_date });
  };

  const handleCurrencyUpdate = async (
    e: React.MouseEvent<HTMLDivElement>,
    fileId: number,
    currentCurrency: string | null
  ) => {
    e.stopPropagation();
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: CurrencyPrompt,
      props: { currentCurrency }
    });
    if (!result) return;
    await updateClientWorkspaceFileAction({ fileId, currency: result.currency });
    patchDetail(fileId, EntityType.FILE, { currency: result.currency });
  };

  return {
    handleClassificationUpdate,
    handleOperationsStatusUpdate,
    handleBulkClassificationUpdate,
    handleBulkStatusUpdate,
    handleInvestorUpdate,
    handleTagsSync,
    handleEffectiveDateUpdate,
    handleCurrencyUpdate,
    // Sharing isn't supported in the client workspace — required by the override shape.
    handleShareUpdate: () => {}
  };
};
